import { Link } from "react-router-dom";
import {
  Flame,
  Sparkles,
  Wrench,
  Leaf,
  ShieldCheck,
  Timer,
} from "lucide-react";
import { Seo, breadcrumbSchema, faqSchema, serviceSchema } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { useSiteContent } from "../lib/useSiteContent";
import { ChecklistSection } from "../components/ChecklistSection";
import { WhyChooseUs } from "../components/WhyChooseUs";
import { ProcessSteps } from "../components/ProcessSteps";
import { FaqAccordion } from "../components/FaqAccordion";
import { RelatedLinks } from "../components/RelatedLinks";
import { ServiceCTA } from "../components/ServiceCTA";

const PATH = "/oven-cleaning-london";

const DEFAULT_FAQS = [
  {
    question:
      "How does professional oven cleaning in London differ from DIY?",
    answer:
      "DIY oven cleaning relies on caustic supermarket sprays applied while the oven is in place — they reach only the surfaces you can see and leave the door glass, fan housing and back panel mostly untouched. Professional oven cleaning uses a heated dip-tank: we remove the racks, side liners, fan covers and door glass, soak them in a non-caustic degreaser away from the property, then hand-clean the cavity in place. The result is restored to near-new condition with no chemical residue inside the oven.",
  },
  {
    question:
      "How long does a professional oven clean take in a London flat?",
    answer:
      "A single oven typically takes 90 to 120 minutes, a double oven 2 to 3 hours, and a range cooker 3 to 4 hours. Hobs, extractors and microwaves can be added to the same visit and are usually completed inside an extra hour each. We work cleanly with floor protection so you can carry on around us.",
  },
  {
    question:
      "Do I need professional oven cleaning before moving out of my London rental?",
    answer:
      "Yes — and this is the single most common deposit deduction trigger we see. A clean oven is the first item most letting agents check on the inventory checkout report, and the back panel, fan housing and door glass interior are almost impossible to clean to inventory standard without dip-tank degreasing. A receipt for professional oven cleaning is the cleanest way to evidence the work.",
  },
  {
    question:
      "Can you clean self-cleaning, pyrolytic and catalytic ovens safely?",
    answer:
      "Yes. We do not use caustic chemicals inside the cavity of pyrolytic or catalytic ovens — caustic products damage the special enamel that makes those systems work. Our process is dip-tank based for removable parts and uses non-caustic degreaser inside the cavity, which is safe for every oven type including AGA, Neff Slide&Hide, Bosch Pyrolytic and integrated Smeg ranges.",
  },
  {
    question: "Are the products you use safe for children and pets?",
    answer:
      "Yes. Our dip-tank chemistry is non-caustic, low-fume and biodegradable. Inside the property we use plant-based degreasers that leave no toxic residue, so the oven is safe to use within minutes of completion — including for households with babies and pets.",
  },
  {
    question:
      "Will move out oven cleaning include the hob and extractor as well?",
    answer:
      "We price each appliance separately so you only pay for what your tenancy needs. The most common booking for a checkout is oven + hob + extractor filter, which together cover every appliance an inventory clerk inspects in detail. Microwaves and standalone grills can be added for a small extra charge.",
  },
];

const CHECKLIST_GROUPS = [
  {
    title: "Single Oven",
    items: [
      "Remove racks, trays and side liners",
      "Dip-tank degrease all removable parts off-site",
      "Hand-clean cavity, ceiling, back and floor of oven",
      "Clean inside and between door glass panels",
      "Re-fit parts and test the door seal",
      "Polish exterior, controls and handle",
    ],
  },
  {
    title: "Double / Range Oven",
    items: [
      "Both ovens treated with separate dip cycles",
      "Cavity and roof panels degreased and polished",
      "Fan housings and elements cleaned around",
      "Storage compartments and warming drawers cleaned",
      "Grill chambers degreased and trays restored",
      "Knobs, handles and ranges polished",
    ],
  },
  {
    title: "Hob & Extractor",
    items: [
      "Gas, induction or ceramic hob descaled and polished",
      "Pan supports and burner caps dip-tanked",
      "Extractor filters dip-tanked or replaced if requested",
      "Extractor canopy and grease tray wiped",
      "Splashback and tile grout degreased",
      "Worktop edges around hob detail cleaned",
    ],
  },
  {
    title: "Microwave & Add-Ons",
    items: [
      "Microwave interior and turntable cleaned",
      "Microwave door glass and frame polished",
      "Standalone grill and toaster oven interiors",
      "BBQ deep clean as a bookable add-on",
      "AGA, Rayburn and range covers wiped",
      "Final 'sparkle' walkthrough with you on site",
    ],
  },
];

const getWhyChoose = (sc) => [
  { icon: Flame,      title: sc.why1_title || "Dip-tank deep clean",       description: sc.why1_desc || "We do not just spray and wipe. Racks, side liners and door glass are removed and soaked in our heated dip-tank for a true restoration finish." },
  { icon: Sparkles,   title: sc.why2_title || "Like-new finish",            description: sc.why2_desc || "The back panel, fan housing, door glass interior and seal are all restored — the parts inventory clerks photograph during a checkout report." },
  { icon: Wrench,     title: sc.why3_title || "Pyrolytic & AGA safe",       description: sc.why3_desc || "Trained on Neff Slide&Hide, AGA, Rayburn, Smeg ranges and modern pyrolytic / catalytic ovens — no caustic damage, no warranty issues." },
  { icon: Leaf,       title: sc.why4_title || "Non-caustic, low-fume",      description: sc.why4_desc || "Biodegradable chemistry that is safe for kitchens shared with babies, toddlers, pets and households sensitive to harsh fumes." },
  { icon: ShieldCheck,title: sc.why5_title || "Fully insured cleaners",     description: sc.why5_desc || "£5M public liability cover, DBS-vetted staff and floor protection used on every job — peace of mind for tenants and landlords alike." },
  { icon: Timer,      title: sc.why6_title || "Same-day move-out slots",    description: sc.why6_desc || "Tied to a London checkout? We routinely fit in oven-only jobs the same day or evening before the inventory clerk arrives." },
];

const getProcess = (sc) => [
  { title: sc.step1_title || "Quick quote",          description: sc.step1_desc || "Tell us your appliances and postcode. Each oven, hob and extractor is priced separately so you only pay for what your tenancy needs." },
  { title: sc.step2_title || "Floor protection",     description: sc.step2_desc || "On arrival we lay protective sheeting under the oven and across the floor path. No grease or product touches your kitchen surfaces." },
  { title: sc.step3_title || "Dip-tank degrease",   description: sc.step3_desc || "All removable parts are taken to our outside-property dip-tank while the cavity is hand-cleaned inside the kitchen." },
  { title: sc.step4_title || "Re-assemble & polish", description: sc.step4_desc || "Every part is rinsed, dried and refitted. We test the door seal, polish the exterior and walk you through the result before we leave." },
];

const OvenCleaningLondon = () => {
  const sc = useSiteContent("oven", {
    eyebrow: "Oven Cleaning London", h1: "Professional Oven Cleaning", highlight: "in London",
    subheading: "Dip-tank degreasing for single, double and range ovens, hobs, extractors and microwaves — restored to a like-new finish in a single visit.",
    overview_eyebrow: "Service Overview", overview_heading: "The London oven clean that actually clears a checkout report",
    overview_p1: "The oven is the single most-photographed appliance on a London inventory report. Every inventory clerk knows that a tenant has often spent ten minutes wiping the door glass and an hour avoiding the back panel — and that is exactly what they photograph at check-in and check-out. A professional oven cleaner does the job a kitchen sponge cannot: removes the parts, takes them outside the property, and soaks them clean rather than scrubbing into a flat surface.",
    overview_p2: "Our process is built around a heated dip-tank. Racks, side liners, fan covers and the inner door glass are removed and soaked in a non-caustic, low-fume degreaser away from your kitchen. While they soak, our cleaner hand-treats the cavity, fan housing, oven floor and door seal inside the kitchen. Parts are rinsed, dried, refitted and polished — and the finished oven is safe to use again within minutes because no caustic residue is ever applied inside the appliance.",
    overview_p3: "We clean every type of oven sold in the UK, from budget-built electric singles up to AGA ranges, Neff Slide&Hide pyrolytic models and integrated Smeg dual-fuel ranges. If your booking is part of a wider checkout, pair this service with our specialist",
    checklist_eyebrow: "What's Included", checklist_heading: "The full London oven cleaning specification", checklist_sub: "Every appliance is priced individually. Most checkout bookings combine the oven, hob and extractor — together that is everything an inventory clerk inspects in detail.",
    why_eyebrow: "Why Choose Us", why_heading: "London's most reliable professional oven cleaners", why_sub: "The right chemistry on the right oven type, applied with the right process — that's what separates a restored oven from a wiped one.",
    why1_title: "Dip-tank deep clean",    why1_desc: "We do not just spray and wipe. Racks, side liners and door glass are removed and soaked in our heated dip-tank for a true restoration finish.",
    why2_title: "Like-new finish",         why2_desc: "The back panel, fan housing, door glass interior and seal are all restored — the parts inventory clerks photograph during a checkout report.",
    why3_title: "Pyrolytic & AGA safe",    why3_desc: "Trained on Neff Slide&Hide, AGA, Rayburn, Smeg ranges and modern pyrolytic / catalytic ovens — no caustic damage, no warranty issues.",
    why4_title: "Non-caustic, low-fume",   why4_desc: "Biodegradable chemistry that is safe for kitchens shared with babies, toddlers, pets and households sensitive to harsh fumes.",
    why5_title: "Fully insured cleaners",  why5_desc: "£5M public liability cover, DBS-vetted staff and floor protection used on every job — peace of mind for tenants and landlords alike.",
    why6_title: "Same-day move-out slots", why6_desc: "Tied to a London checkout? We routinely fit in oven-only jobs the same day or evening before the inventory clerk arrives.",
    process_eyebrow: "The Process", process_heading: "From the doormat to a sparkling oven in four steps",
    step1_title: "Quick quote",          step1_desc: "Tell us your appliances and postcode. Each oven, hob and extractor is priced separately so you only pay for what your tenancy needs.",
    step2_title: "Floor protection",     step2_desc: "On arrival we lay protective sheeting under the oven and across the floor path. No grease or product touches your kitchen surfaces.",
    step3_title: "Dip-tank degrease",   step3_desc: "All removable parts are taken to our outside-property dip-tank while the cavity is hand-cleaned inside the kitchen.",
    step4_title: "Re-assemble & polish", step4_desc: "Every part is rinsed, dried and refitted. We test the door seal, polish the exterior and walk you through the result before we leave.",
    spec_eyebrow: "Move out oven cleaning", spec_heading: "The deposit-saver for London tenants",
    spec_p1: "If we had to pick one item that most often costs London tenants part of their deposit, it would be the oven. It is also the single most fixable issue with a professional visit. A dedicated oven clean usually costs less than a typical deposit deduction for a \"dirty oven\" line item — so the booking pays for itself even on a worst-case checkout.",
    spec_p2: "For a complete move-out, combine oven cleaning with our",
    spec_sidebar: "Appliances we clean every week",
    faq_heading: "Oven cleaning London FAQs", faq_sub: "Frequently asked questions about professional oven cleaning in London.",
    faq1_q: DEFAULT_FAQS[0].question, faq1_a: DEFAULT_FAQS[0].answer,
    faq2_q: DEFAULT_FAQS[1].question, faq2_a: DEFAULT_FAQS[1].answer,
    faq3_q: DEFAULT_FAQS[2].question, faq3_a: DEFAULT_FAQS[2].answer,
    faq4_q: DEFAULT_FAQS[3].question, faq4_a: DEFAULT_FAQS[3].answer,
    faq5_q: DEFAULT_FAQS[4].question, faq5_a: DEFAULT_FAQS[4].answer,
    faq6_q: DEFAULT_FAQS[5].question, faq6_a: DEFAULT_FAQS[5].answer,
    cta_title: "Book oven cleaning in London", cta_desc: "Fixed per-appliance pricing, dip-tank deep clean and non-caustic chemistry. Same-day and weekend slots available.",
  });
  const WHY_CHOOSE = getWhyChoose(sc);
  const PROCESS    = getProcess(sc);
  const FAQS = [
    { question: sc.faq1_q, answer: sc.faq1_a }, { question: sc.faq2_q, answer: sc.faq2_a },
    { question: sc.faq3_q, answer: sc.faq3_a }, { question: sc.faq4_q, answer: sc.faq4_a },
    { question: sc.faq5_q, answer: sc.faq5_a }, { question: sc.faq6_q, answer: sc.faq6_a },
  ];
  return (
    <>
      <Seo
        title="Oven Cleaning London — Professional Oven Cleaners"
        description="Professional oven cleaning London. Dip-tank degreasing for single, double and range ovens, hobs and extractors. Move out oven cleaning by an insured local team."
        ogTitle="Oven Cleaning London | Move Out Oven Cleaners"
        ogDescription="Dip-tank oven cleaning, non-caustic chemistry, like-new finish. Same-day and weekend slots across London."
        path={PATH}
        schema={[
          breadcrumbSchema([
            { name: "Services", path: PATH },
            { name: "Oven Cleaning London", path: PATH },
          ]),
          faqSchema(FAQS),
          serviceSchema({
            name: "Oven Cleaning London",
            serviceType: "Oven Cleaning",
            description:
              "Professional dip-tank oven cleaning London — single, double and range ovens, hobs, extractors and microwaves.",
          }),
        ]}
      />

      <PageHero
        eyebrow={sc.eyebrow}
        h1={sc.h1}
        highlight={sc.highlight}
        subheading={sc.subheading}
        breadcrumbs={[
          { name: "Services", path: PATH },
          { name: "Oven Cleaning London", path: PATH },
        ]}
        image="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1920&q=80"
      />

      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="max-w-3xl">
            <span className="text-teal font-semibold text-sm uppercase tracking-wider">{sc.overview_eyebrow}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mt-3 mb-6 leading-tight">{sc.overview_heading}</h2>
            <div className="text-slate-700 text-base md:text-lg leading-relaxed space-y-5">
              <p>{sc.overview_p1}</p>
              <p>{sc.overview_p2}</p>
              <p>
                {sc.overview_p3}{" "}
                <Link to="/end-of-tenancy-cleaning-london" className="text-teal font-medium hover:text-navy">
                  end of tenancy cleaning in London
                </Link>{" "}
                so a single team handles the whole inventory checklist.
              </p>
            </div>
          </div>
        </div>
      </section>

      <ChecklistSection eyebrow={sc.checklist_eyebrow} heading={sc.checklist_heading} subheading={sc.checklist_sub} groups={CHECKLIST_GROUPS} background="slate" />
      <WhyChooseUs eyebrow={sc.why_eyebrow} heading={sc.why_heading} subheading={sc.why_sub} items={WHY_CHOOSE} />
      <ProcessSteps eyebrow={sc.process_eyebrow} heading={sc.process_heading} steps={PROCESS} background="slate" />

      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 max-w-6xl mx-auto items-start">
            <div>
              <span className="text-teal font-semibold text-sm uppercase tracking-wider">{sc.spec_eyebrow}</span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mt-3 mb-5 leading-tight">{sc.spec_heading}</h2>
              <p className="text-slate-700 leading-relaxed mb-4">{sc.spec_p1}</p>
              <p className="text-slate-700 leading-relaxed">
                {sc.spec_p2}{" "}
                <Link to="/carpet-cleaning-london" className="text-teal font-medium hover:text-navy">carpet cleaning in London</Link>{" "}
                and the full{" "}
                <Link to="/end-of-tenancy-cleaning-london" className="text-teal font-medium hover:text-navy">end of tenancy clean</Link>{" "}
                — that bundle covers every inventory line a London letting agent will pull you up on.
              </p>
            </div>

            <div className="bg-slate-50 rounded-2xl p-8 md:p-10 border border-slate-100">
              <h3 className="font-heading text-xl font-semibold text-navy mb-5">{sc.spec_sidebar}</h3>
              <ul className="space-y-3">
                {[
                  "Built-in single electric ovens",
                  "Double ovens and split-cavity models",
                  "Range cookers — AGA, Rayburn, Smeg, Stoves",
                  "Pyrolytic & catalytic ovens (Neff, Bosch, Siemens)",
                  "Induction, ceramic and gas hobs",
                  "Cooker hoods, extractor filters & canopies",
                  "Microwave ovens (combi & standalone)",
                  "Counter-top BBQ grills (add-on)",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                    <span className="text-slate-700 text-sm leading-relaxed">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <FaqAccordion heading={sc.faq_heading} subheading={sc.faq_sub} faqs={FAQS} background="slate" />
      <RelatedLinks currentPath={PATH} />
      <ServiceCTA title={sc.cta_title} description={sc.cta_desc} variant="navy" />
    </>
  );
};

export default OvenCleaningLondon;
